import * as z from "zod";

export const refulfillmentDashboardCellSchema = z.object({
  open: z.number(),
  total: z.number(),
});

export const refulfillmentDashboardMatrixRowSchema = z.object({
  /** ISO instant the one-hour slot starts at; unique within the matrix. */
  start: z.string(),
  /** Local hour of day at `start`, for the label; repeats within the warehouse day. */
  hour: z.number().int().min(0).max(23),
  needsPicking: refulfillmentDashboardCellSchema,
  needsPacking: refulfillmentDashboardCellSchema,
  blocked: refulfillmentDashboardCellSchema,
  total: refulfillmentDashboardCellSchema,
});

export const refulfillmentDashboardNeedsAttentionSchema = z.object({
  distinct: z.number(),
  unableToFulfill: z.number(),
  unableToPick: z.number().nullable(),
  partiallyPickable: z.number().nullable(),
  onHold: z.number(),
  openOver24h: z.number(),
  pickabilityTruncated: z.boolean(),
});

export const refulfillmentDashboardOutcomesSchema = z.object({
  transferred: z.number(),
  transferredByReason: z.array(
    z.object({ reason: z.string(), count: z.number() }),
  ),
  canceled: z.number(),
});

export const refulfillmentDashboardSectionSchema = z.object({
  picking: z.number(),
  packing: z.number(),
  fulfilledToday: z.object({ orders: z.number(), items: z.number() }),
  matrix: z.array(refulfillmentDashboardMatrixRowSchema),
  needsAttention: refulfillmentDashboardNeedsAttentionSchema,
  outcomes: refulfillmentDashboardOutcomesSchema,
});

export const refulfillmentDashboardMerchantOptionSchema = z.object({
  teamId: z.string(),
  name: z.string(),
});

/** The live board; the history charts come from `getRefulfillmentDashboardHistory`. */
export const getRefulfillmentDashboard = {
  name: "getRefulfillmentDashboard" as const,
  input: z.object({
    teamIds: z.array(z.string()).optional(),
    /** The viewer's IANA timezone; an invalid one falls back to UTC, echoed as `timezone`. */
    timezone: z.string(),
  }),
  output: z.object({
    generatedAt: z.string(),
    timezone: z.string(),
    warehouseDay: z.object({ start: z.string(), end: z.string() }),
    merchantOptions: z.array(refulfillmentDashboardMerchantOptionSchema),
    sameDay: refulfillmentDashboardSectionSchema,
  }),
};

export type RefulfillmentDashboardInput = z.infer<
  (typeof getRefulfillmentDashboard)["input"]
>;
export type RefulfillmentDashboard = z.infer<
  (typeof getRefulfillmentDashboard)["output"]
>;
export type RefulfillmentDashboardCell = z.infer<
  typeof refulfillmentDashboardCellSchema
>;
export type RefulfillmentDashboardMatrixRow = z.infer<
  typeof refulfillmentDashboardMatrixRowSchema
>;
export type RefulfillmentDashboardNeedsAttention = z.infer<
  typeof refulfillmentDashboardNeedsAttentionSchema
>;
export type RefulfillmentDashboardOutcomes = z.infer<
  typeof refulfillmentDashboardOutcomesSchema
>;
export type RefulfillmentDashboardSection = z.infer<
  typeof refulfillmentDashboardSectionSchema
>;
export type RefulfillmentDashboardMerchantOption = z.infer<
  typeof refulfillmentDashboardMerchantOptionSchema
>;
